import { useContext, useEffect, useMemo, useRef } from "react"

import { useTruncate } from "shared/lib/useTruncate"

import { useTabBarContext } from "../context/TabBarContext"
import { TabBarTabContainerContextValue } from "../context/TabBarTabContainerContext"
import { TabBarScrollContext } from "../ui/TabBarScrollGroup"
import { useTabBarTabContainerViewModel } from "./useTabBarTabContainerViewModel"

interface UseTabBarBaseTabViewModelProps {
  eventKey: string
  index: number
  label: string
  count: number | undefined
  truncationThreshold: number | undefined
  isDragging: boolean
  insertBefore: boolean
  insertAfter: boolean
}

export function useTabBarBaseTabViewModel({
  eventKey,
  index,
  label,
  count,
  truncationThreshold,
  isDragging,
  insertBefore,
  insertAfter
}: UseTabBarBaseTabViewModelProps) {
  const { selected } = useTabBarTabContainerViewModel({ eventKey })
  const { onTabSelect } = useTabBarContext()

  const { value: displayLabel } = useTruncate(
    label,
    count !== undefined
      ? label.length + 1
      : truncationThreshold ?? label.length + 1
  )

  const contextValue = useMemo<TabBarTabContainerContextValue>(
    () => ({
      index,
      eventKey,
      isDragging,
      insertBefore,
      insertAfter,
      selected
    }),
    [index, eventKey, selected, isDragging, insertBefore, insertAfter]
  )

  const tabRef = useRef<HTMLDivElement | null>(null)

  const { scrollToTab } = useContext(TabBarScrollContext)
  useEffect(() => {
    if (selected && tabRef.current) {
      scrollToTab(tabRef.current)
    }
  }, [selected, index, scrollToTab])

  const tabId = `tab-${eventKey}`

  return { selected, displayLabel, contextValue, tabRef, tabId, onTabSelect }
}
